<template>
  <JdTooltip
    v-if="!disableTooltip"
    effect="light"
    :placement="placement"
    :content="isOverflow ? tooltipContent : undefined"
:disabled="!isOverflow"
  >
    <div
      ref="divRef"
      :class="['jd-ellipsis-text', $attrs.class]"
      :style="mergedStyle"
    >
      <slot />
    </div>
  </JdTooltip>
  <div
    v-else
    ref="divRef"
    :class="['jd-ellipsis-text', $attrs.class]"
    :style="mergedStyle"
  >
    <slot />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onBeforeUnmount, type CSSProperties, type PropType } from 'vue'
import JdTooltip from '../../../tooltip'

const ellipsisStyle: CSSProperties = {
  position: 'relative',
  display: '-webkit-box',
  textOverflow: 'ellipsis',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-all',
  minWidth: 0,
  whiteSpace: 'normal',
}

export default defineComponent({
  name: 'EllipsisText',
  components: { JdTooltip },
  inheritAttrs: false,
  props: {
    title: { type: [String, Object] as PropType<string | Record<string, unknown>>, default: undefined },
    placement: { type: String as PropType<string>, default: 'top' },
    style: { type: Object as PropType<CSSProperties>, default: () => ({}) },
    disableTooltip: { type: Boolean, default: false },
  },
  setup(props, { slots }) {
    const divRef = ref<HTMLDivElement | null>(null)
    const isOverflow = ref(false)

    const mergedStyle = computed<CSSProperties>(() => ({ ...ellipsisStyle, ...props.style }))

    const tooltipContent = computed(() => props.title)

    let observer: ResizeObserver | null = null

    const checkOverflow = () => {
      const el = divRef.value
      if (!el) return
      isOverflow.value = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth
    }

    watch(divRef, (el) => {
      if (observer) { observer.disconnect(); observer = null }
      if (!el) return
      checkOverflow()
      observer = new ResizeObserver(checkOverflow)
      observer.observe(el)
    }, { flush: 'post', immediate: true })

    onBeforeUnmount(() => { if (observer) { observer.disconnect(); observer = null } })

    return { divRef, isOverflow, mergedStyle, tooltipContent, slots }
  },
})
</script>